import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Styles from "./BankingRecon.module.css";
import ReconHeader from "../components/ReconHeader";
import PortfolioNetBalance from "../components/PortfolioNetBalance";
import ReconStatusDonut from "../components/ReconStatusDonut";
import StatTiles from "../components/StatTiles";
import StatusTabs from "../components/StatusTabs";
import RequisitionTable from "../components/RequisitionTable";
import RequisitionDetailPanel from "../components/RequisitionDetailPanel";
import { mapPortfolio, mapConnectionRequisitions } from "../utils/reconMapper";
import {
  fetchReconOverview,
  fetchReconSupply,
  fetchReconReturn,
  fetchReconSupplySources,
  fetchReconSupplyDestinations,
  exportReconSummary,
} from "../../../Redux/Slices/reconciliationSlice";
import { fetchActiveLedger } from "../../../Redux/Slices/bankingSlice";
import {
  fetchActiveContracts,
  fetchContractDetail,
  clearContractDetail,
} from "../../../Redux/Slices/contractsSlice";
import { publicStateUT } from "../../../Redux/Slices/publicSlice";
import { generateCumulativeReport } from "../../../Services/banking/reconciliationExport";
// import ContractFilters from "../components/ContractFilters";

const BankingRecon = () => {
  const [activeTab, setActiveTab] = useState("ALL");
  const [selectedRow, setSelectedRow] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  // const [selectedContractId, setSelectedContractId] = useState("ALL");
  // const [selectedTrader, setSelectedTrader] = useState("ALL");
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.persistedReducer);
  

  const discomId = useSelector(
    (state) => state?.persistedReducer?.discom?.[0]?.discomId,
  );

  const stateLabel = useSelector(
    (state) => state?.persistedReducer?.company?.stateLabel,
  );
  const stateUT = useSelector((state) => state.publicSlice?.stateUT);
  const stateId = useMemo(
    () => stateUT?.find((s) => s.label === stateLabel)?.id,
    [stateUT, stateLabel],
  );
  const { overview, supply } = useSelector((state) => state.reconciliation);

  const activeLedgerData = useSelector(
    (state) => state.bankingSlice.activeLedgerData,
  );

  const { contractDetail } = useSelector((state) => state.contracts);

  useEffect(() => {
    if (!stateUT) dispatch(publicStateUT());
  }, [dispatch, stateUT]);

  useEffect(() => {
    if (!discomId) return;

    if (stateLabel && !stateUT) return;
    dispatch(fetchReconOverview({ partyId: discomId, stateId }));
    dispatch(fetchReconSupply({ partyId: discomId, groupBy: "day", stateId }));
    dispatch(fetchReconReturn({ partyId: discomId, groupBy: "month", stateId }));
    dispatch(fetchReconSupplySources({ partyId: discomId, stateId }));
    dispatch(fetchReconSupplyDestinations({ partyId: discomId, stateId }));
    dispatch(fetchActiveLedger({ currentPage: 0, size: 100 }));
    dispatch(fetchActiveContracts({ partyId: discomId, stateId }));
  }, [dispatch, discomId, stateId, stateLabel, stateUT]);

  const requisitions = useMemo(
    () => mapConnectionRequisitions(activeLedgerData),
    [activeLedgerData],
  );

  const portfolio = useMemo(
    () => mapPortfolio(overview, supply, requisitions),
    [overview, supply, requisitions],
  );

  const handleSelectRow = (row) => {
    setSelectedRow(row);
    if (row?.id != null) dispatch(fetchContractDetail({ connectionId: row.id, stateId, discomId }));
  };

  const handleCloseDetail = () => {
    setSelectedRow(null);
    dispatch(clearContractDetail());
  };

  const handleExport = async () => {
    // dispatch(exportReconSummary({ fmt: "xlsx" }));
    setIsExporting(true);
    try {
      await generateCumulativeReport(discomId, stateId);
    } catch (err) {
      alert(err.message || "Failed to generate report");
    } finally {
      setIsExporting(false);
    }
  };

  const filteredRows = requisitions.filter(
    (r) => activeTab === "ALL" || r.status === activeTab,
  );
  // .filter((r) => selectedContractId === "ALL" || r.contractId === selectedContractId)
  // .filter((r) => selectedTrader === "ALL" || r.trader === selectedTrader);

  return (
    <div className={Styles.page}>
      <ReconHeader
        dateRange={portfolio.dateRange}
        subtitle={portfolio.subtitle}
        onExport={handleExport}
        isExporting={isExporting}
      />

      {/* <ContractFilters
        contracts={activeContracts || []}
        traders={traders || []}
        selectedContractId={selectedContractId}
        selectedTrader={selectedTrader}
        onContractChange={setSelectedContractId}
        onTraderChange={setSelectedTrader}
      /> */}

      <div className={Styles.summaryRow}>
        <PortfolioNetBalance portfolio={portfolio} />
        <ReconStatusDonut
          statusCounts={portfolio.statusCounts}
          total={portfolio.totalRequisitions}
        />
      </div>

      <StatTiles portfolio={portfolio} />

      <StatusTabs
        active={activeTab}
        counts={portfolio.statusCounts}
        total={portfolio.totalRequisitions}
        shownCount={filteredRows.length}
        onChange={setActiveTab}
      />

      <div className={Styles.tableSection}>
        <RequisitionTable rows={filteredRows} onSelectRow={handleSelectRow} />
      </div>

      <RequisitionDetailPanel
        row={selectedRow}
        onClose={handleCloseDetail}
        contract={contractDetail}
      />
    </div>
  );
};

export default BankingRecon;
